/**
 * Personnel-Station Mapping Utilities
 *
 * Reusable utility functions for managing personnel-station relationships
 * across routes and draft routes.
 */

/**
 * Resolve a personnel id from either a direct structure ({ id, ... }) or a nested
 * instance-personnel structure ({ personnel: { id, ... }, ... }).
 */
export function getPersonnelId(personnel) {
  return personnel?.id ?? personnel?.personnel?.id;
}

/**
 * Split personnel assignments by station ID
 * Separates personnel into those assigned to a specific station vs others
 *
 * @param {Object} pathPersonnelStations - Map of personnelId -> stationId
 * @param {Number|String} stationId - Station ID to filter by
 * @returns {Object} { remove: {}, keep: {} } - Split personnel mappings
 */
export function splitPersonnelByStation(pathPersonnelStations, stationId) {
  if (!pathPersonnelStations) {
    return { remove: {}, keep: {} };
  }

  const result = { remove: {}, keep: {} };

  Object.keys(pathPersonnelStations).forEach((personnelId) => {
    if (pathPersonnelStations[personnelId] === stationId) {
      result.remove[personnelId] = stationId;
    } else {
      result.keep[personnelId] = pathPersonnelStations[personnelId];
    }
  });

  return result;
}

/**
 * Transfer personnel assignments from one station to another
 *
 * @param {Object} sourcePathPersonnel - Source route's pathPersonnelStations
 * @param {Object} targetPathPersonnel - Target route's pathPersonnelStations
 * @param {Number|String} stationId - Station ID being transferred
 * @returns {Object} { sourceUpdated: {}, targetUpdated: {} }
 */
export function transferPersonnelToStation(sourcePathPersonnel, targetPathPersonnel, stationId) {
  const { remove, keep } = splitPersonnelByStation(sourcePathPersonnel, stationId);

  return {
    sourceUpdated: keep,
    targetUpdated: {
      ...(targetPathPersonnel || {}),
      ...remove,
    },
  };
}

/**
 * Get all personnel IDs assigned to a specific station
 *
 * @param {Object} pathPersonnelStations - Map of personnelId -> stationId
 * @param {Number|String} stationId - Station ID to find personnel for
 * @returns {Array<String>} Array of personnel IDs
 */
export function getPersonnelAtStation(pathPersonnelStations, stationId) {
  if (!pathPersonnelStations) return [];

  return Object.keys(pathPersonnelStations).filter((personnelId) => pathPersonnelStations[personnelId] === stationId);
}

/**
 * Validate personnel assignment
 * Checks if personnel can be assigned to a station
 *
 * @param {String|Number} personnelId - Personnel ID
 * @param {String|Number} stationId - Station ID
 * @param {Object} draftRoute - Draft route object
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validatePersonnelAssignment(personnelId, stationId, draftRoute) {
  if (!personnelId) {
    return { isValid: false, error: 'Personnel ID is required' };
  }

  if (!stationId) {
    return { isValid: false, error: 'Station ID is required' };
  }

  if (!draftRoute) {
    return { isValid: false, error: 'Draft route not found' };
  }

  // Check if station exists in route
  const stationExists = draftRoute.stations?.some((s) => s.id === stationId);
  if (!stationExists) {
    return { isValid: false, error: 'Station not found in route' };
  }

  return { isValid: true, error: null };
}

/**
 * Remove personnel from station arrays
 * Removes personnel from both personnelIds and personnels arrays
 *
 * @param {Object} station - Station object
 * @param {String|Number} personnelId - Personnel ID to remove
 * @returns {Object} Updated station object
 */
export function removePersonnelFromStation(station, personnelId) {
  if (!station) return station;

  const updatedStation = { ...station };

  // Remove from personnelIds array
  if (updatedStation.personnelIds) {
    updatedStation.personnelIds = updatedStation.personnelIds.filter((id) => id !== personnelId);
  }

  // Remove from personnels array
  if (updatedStation.personnels) {
    updatedStation.personnels = updatedStation.personnels.filter((p) => {
      const pId = p.id || (p.personnel && p.personnel.id);
      return pId !== personnelId;
    });
  }

  return updatedStation;
}

/**
 * Add personnel to station arrays
 * Adds personnel to both personnelIds and personnels arrays if not already present
 *
 * @param {Object} station - Station object
 * @param {String|Number} personnelId - Personnel ID to add
 * @param {Object} personnelData - Personnel data object (optional)
 * @returns {Object} Updated station object
 */
export function addPersonnelToStation(station, personnelId, personnelData = null) {
  if (!station) return station;

  const updatedStation = { ...station };

  // Initialize arrays if they don't exist
  if (!updatedStation.personnelIds) {
    updatedStation.personnelIds = [];
  }
  if (!updatedStation.personnels) {
    updatedStation.personnels = [];
  }

  // Add to personnelIds if not already present
  if (!updatedStation.personnelIds.includes(personnelId)) {
    updatedStation.personnelIds.push(personnelId);
  }

  // Add to personnels array if data provided and not already present
  if (personnelData) {
    const existingIndex = updatedStation.personnels.findIndex((p) => {
      const pId = p.id || (p.personnel && p.personnel.id);
      return pId === personnelId;
    });

    if (existingIndex === -1) {
      updatedStation.personnels.push(personnelData);
    }
  }

  return updatedStation;
}

/**
 * Get personnel count at a station
 *
 * @param {Object} pathPersonnelStations - Map of personnelId -> stationId
 * @param {Number|String} stationId - Station ID
 * @returns {Number} Count of personnel at station
 */
export function getPersonnelCountAtStation(pathPersonnelStations, stationId) {
  return getPersonnelAtStation(pathPersonnelStations, stationId).length;
}

/**
 * Check if station has any personnel assigned
 *
 * @param {Object} pathPersonnelStations - Map of personnelId -> stationId
 * @param {Number|String} stationId - Station ID
 * @returns {Boolean} True if station has personnel
 */
export function hasPersonnelAtStation(pathPersonnelStations, stationId) {
  return getPersonnelCountAtStation(pathPersonnelStations, stationId) > 0;
}

/**
 * Merge personnel assignments from multiple sources
 * Useful when combining personnel from different routes
 *
 * @param {...Object} personnelMaps - Multiple pathPersonnelStations objects
 * @returns {Object} Merged personnel assignments
 */
export function mergePersonnelAssignments(...personnelMaps) {
  return personnelMaps.reduce((merged, current) => {
    if (!current) return merged;
    return { ...merged, ...current };
  }, {});
}

/**
 * Get stations with their personnel counts
 *
 * @param {Object} pathPersonnelStations - Map of personnelId -> stationId
 * @returns {Object} Map of stationId -> personnel count
 */
export function getStationPersonnelCounts(pathPersonnelStations) {
  if (!pathPersonnelStations) return {};

  const counts = {};

  Object.values(pathPersonnelStations).forEach((stationId) => {
    counts[stationId] = (counts[stationId] || 0) + 1;
  });

  return counts;
}
